/**
 * Product list section: header prev/next controls for peek-carousel Flickity instance.
 */
(function () {
  const POSITION_EPSILON = 4;
  const DESKTOP_MEDIA = "(min-width: 768px)";
  const WHEEL_SETTLE_MS = 150;
  const FLICKITY_NAV_EVENTS = ["ready", "select", "change", "scroll", "settle", "dragEnd", "resize"];

  function getFlickity(slider) {
    if (typeof window.Flickity === "undefined" || !slider) return null;
    return window.Flickity.data(slider);
  }

  function getMaxScroll(flkty) {
    const viewport = flkty.size.innerWidth;
    return Math.max(0, flkty.slideableWidth - viewport);
  }

  function getScrollOffset(flkty) {
    if (typeof flkty.x === "number" && Number.isFinite(flkty.x)) {
      const xOffset = Math.abs(flkty.x);
      if (xOffset >= POSITION_EPSILON) return xOffset;
    }

    if (flkty.slider) {
      const transform = getComputedStyle(flkty.slider).transform;
      if (transform && transform !== "none") {
        const matrix = new DOMMatrixReadOnly(transform);
        return Math.abs(matrix.m41);
      }
    }

    return typeof flkty.x === "number" ? Math.abs(flkty.x) : 0;
  }

  function getStartTarget(flkty) {
    const firstSlide = flkty.slides?.[0];
    return firstSlide ? Math.abs(firstSlide.target) : 0;
  }

  function getEndTarget(flkty) {
    const lastSlide = flkty.getLastSlide?.() || flkty.slides?.[flkty.slides.length - 1];
    if (!lastSlide) return getMaxScroll(flkty);
    return Math.abs(lastSlide.target);
  }

  function isAtStart(flkty) {
    const scrollOffset = getScrollOffset(flkty);
    const startTarget = getStartTarget(flkty);

    if (scrollOffset <= startTarget + POSITION_EPSILON) {
      return flkty.selectedIndex === 0;
    }

    return false;
  }

  function isAtEnd(flkty) {
    if (!flkty.slides?.length) return true;

    const scrollOffset = getScrollOffset(flkty);
    const endTarget = getEndTarget(flkty);

    if (endTarget <= POSITION_EPSILON) return true;

    if (scrollOffset >= endTarget - POSITION_EPSILON) return true;

    if (flkty.selectedIndex >= flkty.slides.length - 1) {
      const selectedTarget = Math.abs(flkty.selectedSlide?.target ?? endTarget);
      return scrollOffset >= selectedTarget - POSITION_EPSILON;
    }

    return false;
  }

  function syncNavState(section) {
    const slider = section.querySelector(".product-list__slider");
    const prevBtn = section.querySelector('[data-product-list-nav="prev"]');
    const nextBtn = section.querySelector('[data-product-list-nav="next"]');
    const flkty = getFlickity(slider);

    if (!prevBtn || !nextBtn || !flkty) return;

    prevBtn.disabled = isAtStart(flkty);
    nextBtn.disabled = isAtEnd(flkty);
  }

  function moveNext(flkty) {
    if (isAtEnd(flkty)) return;

    const targetIndex = Math.min(flkty.slides.length - 1, flkty.selectedIndex + 1);
    if (targetIndex === flkty.selectedIndex) return;

    flkty.select(targetIndex);
  }

  function movePrev(flkty) {
    if (isAtStart(flkty)) return;

    const targetIndex = Math.max(0, flkty.selectedIndex - 1);
    if (targetIndex === flkty.selectedIndex) return;

    flkty.select(targetIndex);
  }

  function isDesktopViewport() {
    return window.matchMedia(DESKTOP_MEDIA).matches;
  }

  function isHorizontalWheel(event) {
    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);

    if (absX < 1) return false;
    if (absY > absX && absY > 2) return false;

    return true;
  }

  function clampWheelX(flkty, nextX) {
    if (!flkty.slides?.length || flkty.options.wrapAround) return nextX;

    const maxX = 0;
    const lastSlide = flkty.getLastSlide?.() || flkty.slides[flkty.slides.length - 1];
    const minX = lastSlide ? -Math.abs(lastSlide.target) : 0;

    return Math.max(minX, Math.min(maxX, nextX));
  }

  function applyWheelDelta(flkty, deltaX) {
    flkty.isAnimating = false;
    flkty.velocity = 0;
    flkty.x = clampWheelX(flkty, flkty.x - deltaX);
    flkty.positionSlider();
  }

  function getClosestSlideIndex(flkty) {
    if (!flkty.slides?.length) return 0;

    const restingX = -flkty.x;
    let closestIndex = flkty.selectedIndex;
    let closestDistance = Infinity;

    for (let index = 0; index < flkty.slides.length; index += 1) {
      const distance = flkty.getSlideDistance(restingX, index);
      if (distance === null) continue;

      const absDistance = Math.abs(distance);
      if (absDistance < closestDistance) {
        closestDistance = absDistance;
        closestIndex = index;
      }
    }

    return closestIndex;
  }

  function detachDesktopWheelScroll(slider) {
    const flkty = getFlickity(slider);
    const viewport = flkty?.viewport;

    if (viewport?._productListWheelHandler) {
      viewport.removeEventListener("wheel", viewport._productListWheelHandler);
      delete viewport._productListWheelHandler;
    }

    delete slider.dataset.productListWheelFlktyId;
  }

  function attachDesktopWheelScroll(section, slider, flkty) {
    const viewport = flkty?.viewport;
    if (!viewport) return;

    const flktyId = String(flkty.element.flickityGUID || "");
    if (slider.dataset.productListWheelFlktyId === flktyId) return;

    detachDesktopWheelScroll(slider);

    let settleTimer = null;

    const onWheel = (event) => {
      if (!isDesktopViewport() || !flkty.isDraggable) return;
      if (!isHorizontalWheel(event)) return;

      event.preventDefault();

      applyWheelDelta(flkty, event.deltaX);

      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const targetIndex = getClosestSlideIndex(flkty);
        flkty.select(targetIndex, false, targetIndex === flkty.selectedIndex);
        syncNavState(section);
      }, WHEEL_SETTLE_MS);
    };

    viewport._productListWheelHandler = onWheel;
    viewport.addEventListener("wheel", onWheel, { passive: false });
    slider.dataset.productListWheelFlktyId = flktyId;
  }

  function attachFlickityNav(section, slider) {
    const flkty = getFlickity(slider);
    if (!flkty) return false;

    const flktyId = String(flkty.element.flickityGUID || "");
    if (slider.dataset.productListNavFlktyId === flktyId) {
      attachDesktopWheelScroll(section, slider, flkty);
      syncNavState(section);
      return true;
    }

    const onCarouselUpdate = () => syncNavState(section);
    FLICKITY_NAV_EVENTS.forEach((eventName) => {
      flkty.on(eventName, onCarouselUpdate);
    });

    slider.dataset.productListNavFlktyId = flktyId;
    attachDesktopWheelScroll(section, slider, flkty);
    syncNavState(section);
    return true;
  }

  function scheduleFlickityNavAttach(section, slider) {
    if (slider.dataset.productListNavPolling === "true") return;
    slider.dataset.productListNavPolling = "true";

    let attempts = 0;
    const poll = setInterval(() => {
      if (attachFlickityNav(section, slider) || ++attempts > 50) {
        clearInterval(poll);
        delete slider.dataset.productListNavPolling;
      }
    }, 100);
  }

  function bindSection(section) {
    const slider = section.querySelector(".product-list__slider");
    const prevBtn = section.querySelector('[data-product-list-nav="prev"]');
    const nextBtn = section.querySelector('[data-product-list-nav="next"]');

    if (!slider || !prevBtn || !nextBtn) return;

    if (section.dataset.productListClicksBound !== "true") {
      prevBtn.addEventListener("click", () => {
        const flkty = getFlickity(slider);
        if (flkty) {
          movePrev(flkty);
          requestAnimationFrame(() => syncNavState(section));
        }
      });

      nextBtn.addEventListener("click", () => {
        const flkty = getFlickity(slider);
        if (flkty) {
          moveNext(flkty);
          requestAnimationFrame(() => syncNavState(section));
        }
      });

      section.dataset.productListClicksBound = "true";
    }

    if (!attachFlickityNav(section, slider)) {
      scheduleFlickityNavAttach(section, slider);
    }

    section.dataset.productListBound = "true";
  }

  function resetSection(section) {
    const slider = section.querySelector(".product-list__slider");
    if (slider) {
      detachDesktopWheelScroll(slider);
      delete slider.dataset.productListNavFlktyId;
      delete slider.dataset.productListNavPolling;
    }
    delete section.dataset.productListBound;
  }

  function initAll(root) {
    (root || document)
      .querySelectorAll(".section-product-list, .section-product-recommendations")
      .forEach(bindSection);
  }

  window.ProductListSection = {
    bind: bindSection,
    initAll: initAll,
    reset: resetSection,
    sync: syncNavState,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (
      event.target?.classList?.contains("section-product-list") ||
      event.target?.classList?.contains("section-product-recommendations")
    ) {
      resetSection(event.target);
      bindSection(event.target);
    }
  });
})();
